import type { RowNetworkKey } from "../ids/row-network-key.ts";

/**
 * # Does this structural rebind need to co-hide its rendered columns?
 *
 * A same-sheet structural rebind (`Sheet.rebindStructurePass`) commits new row
 * geometry synchronously inside `bindSheet`'s mutate, while the cell content
 * drains over later frames. In the window between, a rendered column is laid
 * out at the NEW geometry while still painting the OLD content — so the pass
 * marks every rendered column `pending-layout` (`visibility: hidden`) up front
 * and lets each column's `onBind` clear its own mark. That is the "gap G4"
 * co-hide, and it is correct whenever the hazard is real.
 *
 * It was applied unconditionally, and for an APPEND the hazard is not real:
 * adding a row at the end moves no existing row, so nothing on screen is stale
 * and hiding everything buys nothing. What it costs is visible — measured on
 * dev 2026-08-25, one New Row blanked all 12 rendered columns for ~34ms with
 * `render-next` on and ~103ms with it off. That is the operator's "the entire
 * page flashes".
 *
 * The mark is per-COLUMN and every column spans every row, so this cannot be
 * narrowed per row: the decision is all-or-nothing for the pass. Either some
 * already-rendered row moves or resizes — in which case every column really is
 * about to show stale content and all of them must hide — or none does, and
 * none should. This module answers exactly that question, and nothing else.
 *
 * Pure: it compares committed geometry and touches no DOM, so the decision is
 * unit-testable (`sheets/src/render/structural-cohide.deno.test.ts`) and the DOM
 * half stays a single `decide(...)` call.
 */

/**
 * Whether a structural rebind pass must co-hide the rendered columns.
 *
 * There is deliberately no boolean and no nullable form. A caller reading
 * `false` cannot tell "I proved nothing moved" from "I didn't look", and the
 * reason is what a breadcrumb needs in order to explain a flash after the fact.
 */
export type StructuralCoHide =
  | {
    readonly kind: "required";
    readonly reason: StructuralCoHide.Reason;
    /** The first row that forced it — what a diagnostic should name. */
    readonly rowId: RowNetworkKey;
  }
  | { readonly kind: "unnecessary" };

/** Types and values that go with `StructuralCoHide`: `RowGeometry`, `Reason`, `decide`. */
export namespace StructuralCoHide {
  /** A row's committed vertical geometry — the only inputs that decide this. */
  export interface RowGeometry {
    readonly offsetPx: number;
    readonly cellHeightPx: number;
  }

  /** Why the columns have to hide. Each names a way on-screen content goes stale. */
  export type Reason =
    /** An already-rendered row is laid out at a different offset. */
    | "row-moved"
    /** An already-rendered row keeps its offset but changes height. */
    | "row-resized"
    /** An already-rendered row is gone; its painted cells are orphaned. */
    | "row-removed";

  /**
   * Below this, a difference is layout float drift, not a move. Fractional
   * strides (viewport ÷ N, retina half-pixels) make exact equality useless
   * here: without a tolerance every pass would compare unequal, every pass
   * would co-hide, and the narrowing would be inert. Half a hundredth of a
   * pixel is far below anything that can visibly misalign, and far above the
   * ~1e-7 drift the arithmetic actually produces.
   */
  const EPSILON_PX = 0.01;

  const UNNECESSARY: StructuralCoHide = { kind: "unnecessary" };

  const moved = (a: number, b: number): boolean => Math.abs(a - b) >= EPSILON_PX;

  /**
   * Compare the geometry currently on screen (`before` — what the rendered
   * columns are painting) against the geometry this pass is about to commit
   * (`after`).
   *
   * Only rows in `before` can go stale: a row that is not rendered yet has no
   * painted cells to misplace, which is precisely why an append comes back
   * `unnecessary`. Rows added in `after` are ignored by construction.
   */
  export function decide(
    before: ReadonlyMap<RowNetworkKey, RowGeometry>,
    after: ReadonlyMap<RowNetworkKey, RowGeometry>,
  ): StructuralCoHide {
    for (const [rowId, prev] of before) {
      // A row that paints nothing cannot paint anything STALE. This is not a
      // micro-optimisation, it is the difference between the narrowing working
      // and being inert: measured on dev 2026-08-25, appending a row moves
      // exactly one entry in the committed layout — `system(surface|footer)`,
      // the footer surface sentinel, which sits at the bottom with
      // `cellHeightPx` 0 and shifts by the new row's height. Counting that as a
      // move made every append co-hide anyway. The inverse is deliberately NOT
      // skipped: a row COLLAPSING from a real height to zero was painting a
      // moment ago, so it is judged below like any other change.
      if (prev.cellHeightPx < EPSILON_PX) continue;
      const next = after.get(rowId);
      if (next == null) return { kind: "required", reason: "row-removed", rowId };
      if (moved(prev.offsetPx, next.offsetPx)) {
        return { kind: "required", reason: "row-moved", rowId };
      }
      if (moved(prev.cellHeightPx, next.cellHeightPx)) {
        return { kind: "required", reason: "row-resized", rowId };
      }
    }
    return UNNECESSARY;
  }
}
